import React, { ReactNode, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useAppData } from '../../context/AppDataContext';
import { permissionService } from '../../services/permissionService';
import { activityService } from '../../services/activityService';
import { 
  FiLayout, 
  FiUsers, 
  FiPackage, 
  FiUserCheck, 
  FiSettings,
  FiLogOut,
  FiUser,
  FiChevronRight,
  FiBell
} from 'react-icons/fi';
import { 
  HiOutlineChartBar,
  HiOutlineUserGroup,
  HiOutlineCube,
  HiOutlineUsers,
  HiOutlineCog,
  HiOutlineTrendingUp,
  HiOutlineClipboardList,
  HiOutlineShoppingCart,
  HiOutlineDocumentText,
  HiOutlineShieldCheck
} from 'react-icons/hi';
import Icon from './Icon';
import NotificationBell from '../notifications/NotificationBell';

interface LayoutProps {
  children: ReactNode;
}

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  hoverColor: string;
  permissionResource?: string; // Resource name for permission check (e.g., 'dashboard', 'leads')
  roles?: string[]; // Fallback for backward compatibility
}

const REPORT_NAV_ITEMS: Array<{ path: string; label: string }> = [
  { path: '/reports/data-calls', label: 'Data Call Report' },
  { path: '/reports/tender-calls', label: 'Tender Call Report' },
  { path: '/reports/customer-calls', label: 'Customer Call Report' },
];

const ADMIN_NAV_ITEMS: Array<{ path: string; label: string }> = [
  { path: '/users', label: 'Users' },
  { path: '/brands', label: 'Brands' },
  { path: '/tender-awardees', label: 'Tender Awardees' },
  { path: '/roles', label: 'Roles' },
  { path: '/settings', label: 'Settings' },
  { path: '/permissions', label: 'Permissions' },
  { path: '/activities', label: 'Activities' },
];

const LISTING_ROUTE_PATHS = new Set([
  '/leads',
  '/tenders',
  '/customers',
  '/orders',
  '/users',
  '/brands',
  '/tender-awardees',
  '/kyc',
  '/csv-import',
  '/activities',
  '/roles',
  '/permissions',
  '/reports/data-calls',
  '/reports/tender-calls',
  '/reports/customer-calls',
]);

function isListingRoute(pathname: string): boolean {
  return LISTING_ROUTE_PATHS.has(pathname);
}

function isInsideEditableField(node: Node | null): boolean {
  if (!node) return false;
  const el = node.nodeType === Node.TEXT_NODE ? (node as Text).parentElement : (node as Element);
  return !!el?.closest('input, textarea, select, [contenteditable="true"]');
}

/** True = user may copy (form field, dialog, or not a listing route — caller handles route). */
function listingCopyIsAllowed(): boolean {
  const sel = typeof window !== 'undefined' ? window.getSelection() : null;
  if (sel && sel.rangeCount > 0 && !sel.isCollapsed) {
    const node = sel.getRangeAt(0).commonAncestorContainer;
    if (isInsideEditableField(node)) return true;
    if (node instanceof Element && node.closest('[role="dialog"]')) return true;
    return false;
  }
  const ae = typeof document !== 'undefined' ? document.activeElement : null;
  if (ae instanceof HTMLInputElement || ae instanceof HTMLTextAreaElement || ae instanceof HTMLSelectElement) {
    return true;
  }
  if (ae instanceof HTMLElement && ae.isContentEditable) return true;
  if (ae instanceof Element && ae.closest('[role="dialog"]')) return true;
  return false;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { permissions: userPermissions } = useAppData();
  const lastLoggedPath = useRef<string | null>(null);

  // Track page visits (prevent duplicate calls from React StrictMode)
  useEffect(() => {
    if (user && location.pathname !== '/login') {
      const pageName = location.pathname;
      // Only log if this is a different page than last logged
      if (lastLoggedPath.current !== pageName) {
        lastLoggedPath.current = pageName;
        activityService.logPageVisit(pageName, `Visited ${pageName}`).catch(err => {
          // Silently fail - don't break the app
          console.error('Failed to log page visit:', err);
        });
      }
    }
  }, [location.pathname, user]);

  // Block copy/cut on listing routes at document capture so table/list text cannot be copied
  // (React handlers on <main> miss cases where focus stays in search while selection is in the grid).
  useEffect(() => {
    if (!isListingRoute(location.pathname)) return;

    const onClipboardBlock = (e: ClipboardEvent) => {
      if (listingCopyIsAllowed()) return;
      e.preventDefault();
    };

    const onKeyBlock = (e: KeyboardEvent) => {
      if (!((e.ctrlKey || e.metaKey) && (e.key === 'c' || e.key === 'C' || e.key === 'x' || e.key === 'X'))) {
        return;
      }
      if (listingCopyIsAllowed()) return;
      e.preventDefault();
    };

    document.addEventListener('copy', onClipboardBlock, true);
    document.addEventListener('cut', onClipboardBlock, true);
    document.addEventListener('keydown', onKeyBlock, true);
    return () => {
      document.removeEventListener('copy', onClipboardBlock, true);
      document.removeEventListener('cut', onClipboardBlock, true);
      document.removeEventListener('keydown', onKeyBlock, true);
    };
  }, [location.pathname]);

  const navItems: NavItem[] = [
    {
      path: '/dashboard',
      label: 'Dashboard',
      icon: <Icon component={HiOutlineChartBar} className="w-5 h-5" />,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      hoverColor: 'hover:bg-yellow-50 hover:text-yellow-600',
      permissionResource: 'dashboard',
      roles: ['org_admin', 'manager', 'executive'] // Fallback
    },
    {
      path: '/leads',
      label: 'Data',
      icon: <Icon component={HiOutlineTrendingUp} className="w-5 h-5" />,
      color: 'text-yellow-700',
      bgColor: 'bg-yellow-100',
      hoverColor: 'hover:bg-yellow-100 hover:text-yellow-700',
      permissionResource: 'leads',
      roles: ['org_admin', 'manager', 'executive']
    },
    {
      path: '/tenders',
      label: 'Tenders',
      icon: <Icon component={HiOutlineClipboardList} className="w-5 h-5" />,
      color: 'text-black-800',
      bgColor: 'bg-yellow-50',
      hoverColor: 'hover:bg-yellow-50 hover:text-black-800',
      permissionResource: 'tenders',
      roles: ['org_admin', 'manager', 'executive']
    },
    {
      path: '/customers',
      label: 'Customers',
      icon: <Icon component={HiOutlineUsers} className="w-5 h-5" />,
      color: 'text-yellow-700',
      bgColor: 'bg-yellow-100',
      hoverColor: 'hover:bg-yellow-100 hover:text-yellow-700',
      permissionResource: 'customers',
      roles: ['org_admin', 'manager', 'executive']
    },
    {
      path: '/orders',
      label: 'Orders',
      icon: <Icon component={HiOutlineShoppingCart} className="w-5 h-5" />,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      hoverColor: 'hover:bg-yellow-50 hover:text-yellow-600',
      permissionResource: 'orders',
      roles: ['org_admin', 'manager', 'executive']
    },
    {
      path: '/kyc',
      label: 'KYC',
      icon: <Icon component={HiOutlineDocumentText} className="w-5 h-5" />,
      color: 'text-yellow-700',
      bgColor: 'bg-yellow-100',
      hoverColor: 'hover:bg-yellow-100 hover:text-yellow-700',
      permissionResource: 'kyc',
      roles: ['org_admin', 'manager', 'executive']
    },
    {
      path: '/csv-import',
      label: 'CSV Import',
      icon: <Icon component={HiOutlineClipboardList} className="w-5 h-5" />,
      color: 'text-black-700',
      bgColor: 'bg-yellow-50',
      hoverColor: 'hover:bg-yellow-50 hover:text-black-700',
      permissionResource: 'csv_import',
      roles: ['org_admin', 'manager']
    }
  ];

  const isActive = (path: string) => {
    if (path === '/leads' && location.pathname.startsWith('/leads')) {
      return true;
    }
    if (path === '/tenders' && location.pathname.startsWith('/tenders')) {
      return true;
    }
    return location.pathname === path;
  };

  const getRoleBadgeColor = (role: string) => {
    const colors: { [key: string]: string } = {
      'org_admin': 'bg-gradient-to-r from-yellow-500 to-yellow-600',
      'manager': 'bg-gradient-to-r from-yellow-400 to-yellow-500',
      'executive': 'bg-gradient-to-r from-yellow-300 to-yellow-400'
    };
    return colors[role] || 'bg-gray-500';
  };

  // Filter nav items based on permissions (or fallback to roles)
  const filteredNavItems = navItems.filter(item => {
    if (item.path === '/roles') {
      return user?.role === 'org_admin';
    }
    // Org admin always sees everything
    if (user?.role === 'org_admin') {
      return true;
    }
    
    // If permissions are loaded, use permission-based filtering
    if (userPermissions && item.permissionResource) {
      return permissionService.canViewMenu(
        userPermissions.menu_permissions,
        item.permissionResource
      );
    }
    
    // Fallback to role-based filtering if permissions not loaded or no permissionResource
    if (item.roles && user) {
      return item.roles.includes(user.role);
    }
    
    // Show item if no restrictions
    return !item.roles && !item.permissionResource;
  });

  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [reportsOpen, setReportsOpen] = React.useState(false);
  const [administrationOpen, setAdministrationOpen] = React.useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-50 w-full">
      <header className="bg-gradient-to-r from-black-950 via-black-900 to-black-800 shadow-md w-full sticky top-0 z-50 border-b-2 border-yellow-500">
        <div className="w-full px-3 sm:px-4 lg:px-6">
          <div className="flex justify-between items-center py-2">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <img 
                src="/images/infrasol-commercial-v2.jpg" 
                alt="Infrasol Logo" 
                className="h-8 sm:h-10 w-auto object-contain"
              />
              <h1 className="text-lg sm:text-xl font-bold text-yellow-400">Infrasol CRM</h1>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-3">
              {/* Mobile: Just circle icon */}
              <div className="sm:hidden">
                <div className={`w-8 h-8 rounded-full ${getRoleBadgeColor(user?.role || '')} flex items-center justify-center text-white text-xs font-bold shadow-sm`}>
                  {user?.name?.charAt(0).toUpperCase() || 'U'}
                </div>
              </div>
              {/* Desktop: Full user info */}
              <div className="hidden sm:flex items-center space-x-1.5 bg-white/20 backdrop-blur-sm rounded-md px-2 py-1.5">
                <div className={`w-7 h-7 rounded-full ${getRoleBadgeColor(user?.role || '')} flex items-center justify-center text-white text-xs font-bold`}>
                  {user?.name?.charAt(0).toUpperCase() || 'U'}
                </div>
                <div className="text-white hidden md:block">
                  <div className="text-xs font-semibold leading-tight">{user?.name}</div>
                  <div className="text-xs opacity-90 leading-tight">{user?.role?.replace('_', ' ').toUpperCase()}</div>
                </div>
              </div>
              <NotificationBell />
              <button
                onClick={logout}
                className="flex items-center space-x-1 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white px-2 sm:px-3 py-1.5 rounded-md transition-all duration-200 text-xs sm:text-sm font-medium"
              >
                <Icon component={FiLogOut} className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Logout</span>
              </button>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="sm:hidden flex items-center justify-center w-8 h-8 bg-white/20 hover:bg-white/30 rounded-md text-white"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {mobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>
          <nav className={`flex flex-wrap items-start gap-1 sm:gap-1.5 border-t border-yellow-500/30 pt-1.5 pb-1.5 ${mobileMenuOpen ? 'flex' : 'hidden sm:flex'}`}>
            {filteredNavItems.map((item) => {
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center space-x-1.5 px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-md transition-all duration-200 text-xs sm:text-sm font-medium ${
                    active
                      ? `${item.bgColor} ${item.color} shadow-sm`
                      : `text-white/80 ${item.hoverColor}`
                  }`}
                >
                  <span className="flex-shrink-0">{item.icon}</span>
                  <span className="whitespace-nowrap">{item.label}</span>
                  {active && <Icon component={FiChevronRight} className="w-3 h-3 ml-0.5 hidden sm:block" />}
                </Link>
              );
            })}
            {(user?.role === 'org_admin' || user?.role === 'manager' || user?.role === 'executive') && (
              <div className="relative w-auto">
                <button
                  type="button"
                  onClick={() => setReportsOpen((v) => !v)}
                  className={`flex items-center space-x-1.5 px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-md transition-all duration-200 text-xs sm:text-sm font-medium ${
                    location.pathname.startsWith('/reports/')
                      ? 'bg-yellow-50 text-yellow-700 shadow-sm'
                      : 'text-white/80 hover:bg-yellow-50 hover:text-yellow-700'
                  }`}
                >
                  <span className="flex-shrink-0">
                    <Icon component={HiOutlineChartBar} className="w-5 h-5" />
                  </span>
                  <span className="whitespace-nowrap">Reports</span>
                  <Icon
                    component={FiChevronRight}
                    className={`w-3 h-3 transition-transform duration-200 ${reportsOpen ? 'rotate-90' : ''}`}
                  />
                </button>

                {/* Click-toggle submenu for both mobile and desktop */}
                {reportsOpen && (
                  <div className="absolute top-full left-0 z-50 mt-1 flex min-w-[200px] max-w-[calc(100vw-1.5rem)] flex-col gap-1 rounded-md border border-yellow-500/30 bg-black-900/95 p-1.5 shadow-lg backdrop-blur">
                    {REPORT_NAV_ITEMS.map((report) => {
                      const active = location.pathname === report.path;
                      return (
                        <Link
                          key={report.path}
                          to={report.path}
                          onClick={() => {
                            setReportsOpen(false);
                            setMobileMenuOpen(false);
                          }}
                          className={`flex items-center px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-md transition-all duration-200 text-xs sm:text-sm ${
                            active
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'text-white/75 hover:bg-yellow-50 hover:text-yellow-700'
                          }`}
                        >
                          {report.label}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            {user?.role === 'org_admin' && (
              <div className="relative w-auto">
                <button
                  type="button"
                  onClick={() => setAdministrationOpen((v) => !v)}
                  className={`flex items-center space-x-1.5 px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-md transition-all duration-200 text-xs sm:text-sm font-medium ${
                    ADMIN_NAV_ITEMS.some((item) => location.pathname === item.path)
                      ? 'bg-yellow-50 text-yellow-700 shadow-sm'
                      : 'text-white/80 hover:bg-yellow-50 hover:text-yellow-700'
                  }`}
                >
                  <span className="flex-shrink-0">
                    <Icon component={HiOutlineCog} className="w-5 h-5" />
                  </span>
                  <span className="whitespace-nowrap">Administration</span>
                  <Icon
                    component={FiChevronRight}
                    className={`w-3 h-3 transition-transform duration-200 ${administrationOpen ? 'rotate-90' : ''}`}
                  />
                </button>

                {administrationOpen && (
                  <div className="absolute top-full left-0 z-50 mt-1 flex min-w-[200px] max-w-[calc(100vw-1.5rem)] flex-col gap-1 rounded-md border border-yellow-500/30 bg-black-900/95 p-1.5 shadow-lg backdrop-blur">
                    {ADMIN_NAV_ITEMS.map((item) => {
                      const active = location.pathname === item.path;
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={() => {
                            setAdministrationOpen(false);
                            setMobileMenuOpen(false);
                          }}
                          className={`flex items-center px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-md transition-all duration-200 text-xs sm:text-sm ${
                            active
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'text-white/75 hover:bg-yellow-50 hover:text-yellow-700'
                          }`}
                        >
                          {item.label}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </nav>
        </div>
      </header>
      <main
        className="w-full px-3 sm:px-4 lg:px-6 py-4 sm:py-6"
        onContextMenu={(e) => {
          if (isListingRoute(location.pathname)) {
            e.preventDefault();
          }
        }}
      >
        <div className="w-full mx-auto">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;

